import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import type { NavigateFunction } from 'react-router'
import { camelCase } from 'lodash'
import { Button, ButtonGroup, ButtonToolbar, Container, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { selectCommon, toSnake, Checkbox, DataList, ButtonModals, ErrorModal } from '../common'
import type { AlertState, FormConfig, DataListColumn } from '../common'
import { useAuth } from '../auth'
import type { CurrentUser } from '../auth'
import { fetchMedia, selectMedia, fetchSettings, selectSetting } from '../media'
import type { Media, Setting } from '../media'
import { fetchSubLayouts, createSubLayout, modifySubLayout, copySubLayout, deleteSubLayout } from './subLayoutAPI'
import { selectSubLayoutItem } from './subLayoutSelectors'
import { selectColumn, selectPage, selectId, unselectId, openModal, closeModal, dismissError } from './subLayoutActions'
import { selectUser, fetchUsers } from '../user'
import type { User } from '../user'
import { SubLayoutUpsertForm, SubLayoutCopyForm, SubLayoutDeleteForm } from '../subLayout'
import type { SubLayout } from './types'
import moment from 'moment'
import { isLayoutEditableStatus, isLayoutViewableStatus } from '../article'
moment.locale('ja')

interface EnumValues {
  statuses: Record<string, string>
  [key: string]: unknown
}

type CreateSubLayoutValues = {
  mediaId: string | number
  settingId: string | number
  assigneeId?: number
  issueDate: string
  numberOfCharactersPerRow: string | number
  numberOfRows: string | number
  numberOfColumns: string | number
  name: string
  layoutNumberOfColumns: number
  layoutNumberOfRows: number
  layoutOrientation: string
  borderLineWidth: string
  borderLineType: string
  lineAlignment: string
  linePlacement: string
  loadedBinaryFile?: { blob: Blob; name: string }
}

type EditSubLayoutValues = {
  layoutableType: string
  layoutableId: number
  mediaId: string | number
  settingId: string | number
  assigneeId: number
  numberOfCharactersPerRow: string | number
  numberOfRows: string | number
  numberOfColumns: string | number
  name: string
  layoutNumberOfColumns: number
  layoutNumberOfRows: number
  layoutOrientation: string
  borderLineWidth: string
  borderLineType: string
  lineAlignment: string
  linePlacement: string
  deleteFile?: boolean
  loadedBinaryFile?: { blob: Blob; name: string }
  lockVersion: number
}

type CreateFormSettingParams = {
  handleCreateSubmit: (values: CreateSubLayoutValues) => void
  fetching: boolean
  enumValues: unknown
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  closeModal: () => { type: string }
  mediaList?: Media[]
  settingList?: Setting[]
  userList?: User[]
  currentUser?: CurrentUser | null
  alert: AlertState | null
}

const convertLinePlacement = (linePlacement: string) => {
  return linePlacement.replace(/P/, '_p')
}

export const getHandleCreateSubLayoutSubmit =
  (
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
    navigate: NavigateFunction,
    createSubLayoutAPI: typeof createSubLayout
  ) =>
  ({
    mediaId,
    settingId,
    assigneeId,
    issueDate,
    numberOfCharactersPerRow,
    numberOfRows,
    numberOfColumns,
    name,
    layoutNumberOfColumns,
    layoutNumberOfRows,
    layoutOrientation,
    borderLineWidth,
    borderLineType,
    lineAlignment,
    linePlacement,
    loadedBinaryFile
  }: CreateSubLayoutValues) => {
    const formData = new window.FormData()
    // console.log('linePlacement', { before: linePlacement, after: convertLinePlacement(linePlacement) })
    const newSubLayout = {
      mediaId,
      assigneeId,
      pageBaseSettingId: settingId,
      issueDate,
      numberOfCharactersPerRow,
      numberOfRows,
      numberOfColumns,
      name,
      layoutNumberOfColumns,
      layoutNumberOfRows,
      layoutOrientation,
      borderLineWidth,
      borderLineType,
      lineAlignment,
      linePlacement: convertLinePlacement(linePlacement)
    }
    formData.append('sub_layout', JSON.stringify(toSnake(newSubLayout)))
    if (loadedBinaryFile !== undefined) {
      const { blob, name } = loadedBinaryFile
      formData.append('file', blob, name)
    }
    dispatch(createSubLayoutAPI({ navigate, formData }))
  }

// Accepts both subLayoutAPI.modifySubLayout and articleAPI.modifySubLayout
type ModifySubLayoutAPI = (params: {
  layoutableType: string
  layoutableId: number
  formData: FormData
}) => ThunkAction<unknown, unknown, unknown, UnknownAction>

export const getHandleEditSubLayoutSubmit =
  (dispatch: ThunkDispatch<unknown, unknown, UnknownAction>, modifySubLayoutAPI: ModifySubLayoutAPI) =>
  ({
    layoutableType,
    layoutableId,
    mediaId,
    settingId,
    assigneeId,
    numberOfCharactersPerRow,
    numberOfRows,
    numberOfColumns,
    name,
    layoutNumberOfColumns,
    layoutNumberOfRows,
    layoutOrientation,
    borderLineWidth,
    borderLineType,
    lineAlignment,
    linePlacement,
    deleteFile,
    loadedBinaryFile,
    lockVersion
  }: EditSubLayoutValues) => {
    const formData = new window.FormData()
    const newSubLayout = {
      mediaId,
      pageBaseSettingId: settingId,
      assigneeId,
      numberOfCharactersPerRow,
      numberOfRows,
      numberOfColumns,
      name,
      layoutNumberOfColumns,
      layoutNumberOfRows,
      layoutOrientation,
      borderLineWidth,
      borderLineType,
      lineAlignment,
      linePlacement: convertLinePlacement(linePlacement),
      lockVersion
    }
    formData.append('sub_layout', JSON.stringify(toSnake(newSubLayout)))
    formData.append('lock_version', String(lockVersion))
    if (deleteFile) {
      formData.append('delete_file', 'true')
    } else if (loadedBinaryFile !== undefined) {
      const { blob, name } = loadedBinaryFile
      formData.append('file', blob, name)
    }
    dispatch(modifySubLayoutAPI({ layoutableType, layoutableId, formData }))
  }

export const getCreateFormSetting = ({
  handleCreateSubmit,
  fetching,
  enumValues,
  dispatch,
  closeModal,
  mediaList,
  settingList,
  userList,
  currentUser,
  alert
}: CreateFormSettingParams): FormConfig => {
  const mediaId = mediaList && mediaList.length > 0 ? mediaList[0].id : ''
  const selectedSettingList =
    typeof mediaId === 'number' ? (settingList ?? []).filter((s) => s.media.id === mediaId) : []
  const selectedColumnSetting = selectedSettingList?.[0]?.columnSetting
  // console.log('selectedColumnSetting', selectedColumnSetting)
  const today = moment(new Date()).format('YYYY-MM-DD')
  return {
    initialValues: {
      mediaId,
      settingId: selectedSettingList?.length > 0 ? selectedSettingList[0].id : '',
      assigneeId: currentUser?.id,
      selectedSettingList,
      numberOfCharactersPerRow: selectedColumnSetting?.numberOfCharactersPerRow || '',
      numberOfRows: selectedColumnSetting?.numberOfRows || '',
      numberOfColumns: selectedColumnSetting?.numberOfColumns || '',
      name: '',
      issueDate: today,
      layoutNumberOfColumns: 1,
      layoutNumberOfRows: 1,
      layoutOrientation: 'vertical',
      useBorderLine: true,
      borderLineWidth: '0.5',
      borderLineType: 'solid',
      lineAlignment: 'center',
      linePlacement: 'l4'
    },
    handleSubmit: handleCreateSubmit,
    validationSchema: SubLayoutUpsertForm.schema,
    Component: SubLayoutUpsertForm,
    props: {
      title: '小組新規作成',
      fetching,
      enumValues,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '作成する',
      mediaList,
      settingList,
      userList,
      currentUser,
      alert
    }
  }
}

export const SubLayoutList = () => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
  const {
    data: subLayoutList,
    currentPage,
    totalPages,
    sort,
    selectedIds,
    openedModal,
    fetching: subLayoutFetching,
    errors,
    alert
  } = useSelector(selectSubLayoutItem)
  const { currentUser } = useAuth()
  const { enumValues } = useSelector(selectCommon) as unknown as { enumValues: EnumValues }
  const { data: mediaList, fetching: mediaFetching } = useSelector(selectMedia)
  const { data: userList, fetching: userFetching } = useSelector(selectUser)
  const { data: settingList, fetching: settingFetching } = useSelector(selectSetting)
  const navigate = useNavigate()
  useEffect(() => {
    dispatch(fetchSubLayouts({ page: currentPage, sort }))
  }, [dispatch, sort, currentPage])
  const [fetching, setFetching] = useState(true)
  const [fetched, setFetched] = useState(false)
  useEffect(() => {
    if (subLayoutFetching || mediaFetching || settingFetching || userFetching) {
      setFetching(true)
    } else {
      setFetching(false)
    }
  }, [subLayoutFetching, mediaFetching, settingFetching, userFetching])

  useEffect(() => {
    if (openedModal !== null && !fetched) {
      dispatch(fetchMedia({ all: true }))
      dispatch(fetchSettings({ all: true }))
      dispatch(fetchUsers({}))
      setFetched(true)
    }
  }, [dispatch, fetched, openedModal])

  const mediaIdsInSettings = new Set(settingList.map((s) => s.media.id))
  const filteredMediaList = mediaList.filter((m) => mediaIdsInSettings.has(m.id))
  const columns: DataListColumn[] = [
    {
      id: 'checkboxColumn',
      meta: { cellProps: { style: { width: '40px', textAlign: 'center' } } },
      cell: (props) => (
        <Checkbox
          checked={selectedIds.includes(parseInt(props.row.original.id))}
          value={props.row.original.id}
          onChange={(value: string, checked: boolean) => dispatch((checked ? selectId : unselectId)(parseInt(value)))}
          disabled={fetching}
        />
      )
    },
    {
      accessorKey: 'name',
      name: '名称',
      cell: (props) => {
        const subLayout = props.row.original as SubLayout
        const issueDate = moment(subLayout.issueDate).format('LL')
        return `${subLayout.media.name} ${issueDate} ${subLayout.name} (${subLayout.draftNumber})`
      },
      sortKey: 'name'
    },
    {
      accessorKey: 'status',
      name: 'ステータス',
      cell: (info: any) => enumValues.statuses[camelCase(info.getValue())],
      sortKey: 'status'
    },
    {
      accessorKey: 'layoutRelated',
      name: '字数/行数/段数',
      cell: (props) => {
        const { numberOfCharactersPerRow, numberOfRows, numberOfColumns } = props.row.original as SubLayout
        return `${numberOfCharactersPerRow}/${numberOfRows}/${numberOfColumns}`
      }
    },
    {
      id: 'subLayoutSizeColumn',
      name: '小組版面',
      cell: (props) => {
        const subLayout = props.row.original as SubLayout
        return `${subLayout.layoutNumberOfColumns}段${subLayout.layoutNumberOfRows}行`
      }
    },
    {
      name: '担当者',
      accessorKey: 'assignee',
      cell: (info: any) => info.getValue().name
    },
    {
      name: '最終更新者',
      accessorKey: 'updatedBy',
      cell: (info: any) => info.getValue().name
    },
    {
      accessorKey: 'updatedAt',
      name: '更新日時',
      cell: (info: any) => moment(info.getValue()).format('LLL'),
      sortKey: 'updatedAt'
    },
    {
      id: 'actionColumn',
      name: '小組詳細',
      cell: (props) => (
        <Form className='d-flex'>
          <ButtonToolbar>
            <ButtonGroup size='sm'>
              <Button
                className='list-button'
                onClick={() => navigate(`/sub_layouts/${props.row.original.id}/articles`)}
              >
                <FontAwesomeIcon icon='list' />
                出稿表
              </Button>
              <Button
                className='layout-button'
                onClick={() => navigate(`/sub_layouts/${props.row.original.id}/layout`)}
                disabled={!isLayoutViewableStatus(props.row.original.status)}
              >
                <FontAwesomeIcon icon='table' />
                紙面
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        </Form>
      )
    }
  ]

  const handleColumnClick = (columnName: string) => () => {
    dispatch(selectColumn(columnName))
  }

  const handlePaginationClick = (page: number) => dispatch(selectPage(page))

  const handleCreateSubmit = getHandleCreateSubLayoutSubmit(dispatch, navigate, createSubLayout)

  const handleEditSubmit = getHandleEditSubLayoutSubmit(dispatch, modifySubLayout)

  const handleCopySubmit = ({ draftNumber }: { draftNumber: number }) => {
    if (selectedIds.length < 1) {
      return
    }

    const id = selectedIds[0]
    dispatch(copySubLayout({ id, draftNumber, navigate }))
  }

  const handleDeleteSubmit = () => {
    if (selectedIds.length < 1) {
      return
    }

    const id = selectedIds[0]
    dispatch(deleteSubLayout({ id }))
  }

  const singleSelectedSubLayout =
    selectedIds.length === 1 ? subLayoutList.find((item) => item.id === selectedIds[0]) : null
  const isSelectedSubLayoutReadable =
    singleSelectedSubLayout &&
    (currentUser?.role === 'group_administrator' || currentUser?.id === singleSelectedSubLayout.assignee.id)
  const isSelectedSubLayoutWritable =
    isSelectedSubLayoutReadable && isLayoutEditableStatus(singleSelectedSubLayout.status)
  const createFormSetting = getCreateFormSetting({
    handleCreateSubmit,
    fetching,
    enumValues,
    dispatch,
    closeModal,
    mediaList: filteredMediaList,
    settingList,
    userList,
    currentUser,
    alert
  })

  const toolSettings = [
    {
      name: 'create',
      button: {
        handleClick: () => {
          dispatch(openModal('create'))
        },
        disabled: fetching || currentUser?.role === 'global_administrator',
        icon: 'plus',
        text: '新規'
      },
      form: createFormSetting
    },
    {
      name: 'edit',
      button: {
        handleClick: () => {
          dispatch(openModal('edit'))
        },
        disabled: fetching || !isSelectedSubLayoutReadable,
        icon: 'pencil',
        text: '編集'
      },
      form: {
        initialValues: () => {
          if (selectedIds.length < 1) {
            return {}
          }
          const subLayoutId = selectedIds[0]
          const found = subLayoutList.find((item) => item.id === subLayoutId)
          if (!found) {
            return {}
          }
          const { linePlacement, ...selectedSubLayout } = found
          const mediaId = selectedSubLayout.media.id
          const issueDate = moment(selectedSubLayout.issueDate).format('YYYY-MM-DD')
          const selectedSettingList = settingList.filter((s) => s.media.id === mediaId)
          const useBorderLine = linePlacement !== 'l0'
          return {
            layoutableType: 'SubLayout',
            layoutableId: subLayoutId,
            mediaId,
            settingId: selectedSubLayout.setting.id,
            assigneeId: selectedSubLayout.assignee.id,
            selectedSettingList,
            useBorderLine,
            ...selectedSubLayout,
            linePlacement: camelCase(linePlacement),
            borderLineWidth: selectedSubLayout?.borderLineWidth || '',
            issueDate
          }
        },
        handleSubmit: handleEditSubmit,
        validationSchema: SubLayoutUpsertForm.schema,
        Component: SubLayoutUpsertForm,
        props: {
          title: '小組編集',
          fetching,
          enumValues,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '変更する',
          mediaList: filteredMediaList,
          settingList,
          userList,
          currentUser,
          alert
        }
      }
    },
    {
      name: 'copy',
      button: {
        handleClick: () => {
          dispatch(openModal('copy'))
        },
        disabled: fetching || !isSelectedSubLayoutReadable,
        icon: 'copy',
        text: '複製'
      },
      form: {
        initialValues: () => {
          const selectedSubLayout = subLayoutList.find((item) => item.id === selectedIds[0])
          if (!selectedSubLayout) {
            return {}
          }
          return {
            name: selectedSubLayout.name,
            draftNumber: selectedSubLayout.draftNumber + 1
          }
        },
        handleSubmit: handleCopySubmit,
        validationSchema: SubLayoutCopyForm.schema,
        Component: SubLayoutCopyForm,
        props: {
          title: '小組複製',
          fetching,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '複製する',
          alert
        }
      }
    },
    {
      name: 'delete',
      button: {
        handleClick: () => {
          dispatch(openModal('delete'))
        },
        disabled: fetching || !isSelectedSubLayoutWritable,
        icon: 'trash',
        variant: 'outline-danger',
        text: '削除'
      },
      form: {
        initialValues: () => {
          const selectedSubLayout = subLayoutList.find((item) => item.id === selectedIds[0])
          if (!selectedSubLayout) {
            return {}
          }

          return {
            ...selectedSubLayout
          }
        },
        handleSubmit: handleDeleteSubmit,
        Component: SubLayoutDeleteForm,
        props: {
          title: '小組削除',
          fetching,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '削除する'
        }
      }
    }
  ]
  return (
    <Container fluid>
      <ErrorModal errors={errors} onClose={(errorKey) => dispatch(dismissError(errorKey))} />
      <ButtonModals settings={toolSettings} openedModal={openedModal} notReady={!fetched || fetching} />
      <DataList
        columns={columns}
        data={subLayoutList}
        currentPage={currentPage}
        totalPages={totalPages}
        onColumnClick={handleColumnClick}
        sort={sort}
        onPaginationClick={handlePaginationClick}
      />
    </Container>
  )
}
